import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Skeleton } from '@material-ui/lab';
import { Collapse, IconButton } from '@material-ui/core';
import PaginationActionsTableOmni from './PaginationActionsTableOmni';
import { useCallback } from 'react';

const useStyles = makeStyles(theme => ({
	paper: {
		backgroundColor: `${theme.palette.background.paper} !important`,
		maxWidth: '100vw',
		borderRadius: '16px !important',
		border: `1px solid ${theme.palette.divider}`,
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'hidden'
	},
	header: {
		backgroundColor: '#102239 !important',
		position: 'sticky',
		top: 0,
		zIndex: 10
	},
	tableCell: {
		whiteSpace: 'nowrap',
		padding: '12px 16px',
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	headerCell: {
		color: `${theme.palette.common.white} !important`,
		fontSize: '1.1rem !important',
		backgroundColor: `${theme.palette.background.tableHeader} !important`,
		fontWeight: 600,
		padding: '16px',
		borderBottom: 'none'
	},
	sortLabel: {
		color: `${theme.palette.common.white} !important`,
		'&:hover': {
			color: `${theme.palette.common.white} !important`
		},
		'&.Mui-active': {
			color: `${theme.palette.common.white} !important`
		},
		'& .MuiTableSortLabel-icon': {
			color: `${theme.palette.common.white} !important`
		}
	},
	tableContainer: {
		borderRadius: '16px',
		overflow: 'hidden',
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%'
	},
	tableWrapper: {
		overflow: 'auto',
		flex: 1,
		minHeight: 0, // Critical for flexbox overflow to work
		position: 'relative',
		width: '100%',
		// Custom scrollbar styles
		'&::-webkit-scrollbar': {
			width: '8px',
			height: '8px'
		},
		'&::-webkit-scrollbar-track': {
			background: theme.palette.divider,
			borderRadius: '4px'
		},
		'&::-webkit-scrollbar-thumb': {
			background: theme.palette.action.disabled,
			borderRadius: '4px',
			'&:hover': {
				background: theme.palette.action.selected
			}
		}
	},
	table: {
		minWidth: '100%',
		fontSize: '18px !important',
		borderCollapse: 'separate',
		borderSpacing: 0
	},
	stickyHeader: {
		position: 'sticky',
		top: 0,
		zIndex: 5,
		backgroundColor: theme.palette.background.tableHeader
	},
	tableRow: {
		'&:hover': {
			backgroundColor: `${theme.palette.action.hover} !important`
		},
		cursor: 'pointer'
	},
	pagination: {
		position: 'sticky',
		bottom: 0,
		backgroundColor: theme.palette.background.paper,
		zIndex: 5,
		flexShrink: 0, // Prevent pagination from shrinking
		borderTop: `1px solid ${theme.palette.divider}`,
		padding: '8px 0'
	},
	checkbox: {
		color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff',
		'&.Mui-checked': {
			color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff',
		},
		'&.MuiCheckbox-indeterminate': {
			color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff',
		}
	},
	collapseButton: {
		transition: 'transform 0.3s ease',
	},
	toolbarWithCollapse: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexShrink: 0,
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	collapseContainer: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		overflow: 'hidden'
	},
	emptyState: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '400px',
		flexDirection: 'column',
		gap: '16px'
	}
}));

function descendingComparator(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function getComparator(order, orderBy) {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
	const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, headCells, checkBox } = props;
	const classes = useStyles(props);
	const theme = useTheme();

	const createSortHandler = property => event => {
		onRequestSort(event, property);
	};

	return (
		<TableHead className={classes.stickyHeader}>
			<TableRow className={classes.header}>
				{checkBox && (
					<TableCell 
						style={{ backgroundColor: theme.palette.background.tableHeader }} 
						padding="checkbox"
						className={classes.headerCell}
					>
						<Checkbox
							indeterminate={numSelected > 0 && numSelected < rowCount}
							checked={rowCount > 0 && numSelected === rowCount}
							onChange={onSelectAllClick}
							inputProps={{
								'aria-label': 'select all'
							}}
							className={classes.checkbox}
							style={{
								color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff'
							}}
						/>
					</TableCell>
				)}
				{headCells.map(headCell => (
					<TableCell
						classes={{ root: classes.tableCell }}
						key={headCell.id}
						align={headCell.numeric ? 'center' : 'left'}
						padding={headCell.disablePadding ? 'none' : 'normal'}
						sortDirection={orderBy === headCell.id ? order : false}
						className={classes.headerCell}
					>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : 'asc'}
							onClick={createSortHandler(headCell.id)}
							className={classes.sortLabel}
						>
							{headCell.label}
							{orderBy === headCell.id ? (
								<Box component="span" sx={visuallyHidden}>
									{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
								</Box>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	numSelected: PropTypes.number,
	onRequestSort: PropTypes.func.isRequired,
	onSelectAllClick: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
	rowCount: PropTypes.number.isRequired,
	headCells: PropTypes.array.isRequired
};

function EnhancedTableToolbar(props) {
	const { numSelected, title, collapsible, isCollapsed, onToggleCollapse } = props;
	const classes = useStyles();
	
	return (
		<Toolbar
			sx={[
				{
					pl: { sm: 2 },
					pr: { xs: 1, sm: 1 }
				},
				numSelected > 0 && {
					bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity)
				}
			]}
			className={collapsible ? classes.toolbarWithCollapse : ''}
		>
			{numSelected > 0 ? (
				<Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
					{numSelected} selected
				</Typography>
			) : (
				<Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
					{title}
				</Typography>
			)}
			{collapsible && (
				<IconButton 
					onClick={onToggleCollapse}
					className={classes.collapseButton}
					size="small"
				>
					{isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
				</IconButton>
			)}
		</Toolbar>
	);
}

EnhancedTableToolbar.propTypes = {
	numSelected: PropTypes.number.isRequired,
	title: PropTypes.string,
	collapsible: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	onToggleCollapse: PropTypes.func
};

// Skeleton Component for Table Loading
function TableSkeleton({ headCells, checkBox, rowsPerPage = 5 }) {
	const classes = useStyles();
	const theme = useTheme();

	return (
		<Box className={classes.collapseContainer}>
			<div className={classes.tableWrapper}>
				<Table className={classes.table} aria-labelledby="tableTitle" size="medium" stickyHeader>
					<TableHead className={classes.stickyHeader}>
						<TableRow className={classes.header}>
							{checkBox && (
								<TableCell style={{ backgroundColor: theme.palette.background.tableHeader }} padding="checkbox" className={classes.headerCell}>
									<Skeleton variant="rect" width={24} height={24} animation="wave" />
								</TableCell>
							)}
							{headCells.map(headCell => (
								<TableCell
									key={headCell.id}
									align={headCell.numeric ? 'center' : 'left'}
									className={classes.headerCell}
								>
									<Skeleton variant="text" width="80%" height={24} animation="wave" />
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{[...Array(rowsPerPage)].map((_, rowIndex) => (
							<TableRow key={rowIndex}>
								{checkBox && (
									<TableCell padding="checkbox">
										<Skeleton variant="rect" width={24} height={24} animation="wave" />
									</TableCell>
								)}
								{headCells.map((cell, cellIndex) => (
									<TableCell key={cellIndex} align={cell.numeric ? 'center' : 'left'}>
										<Skeleton variant="text" width="90%" height={20} animation="wave" />
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			<div className={classes.pagination}>
				<div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Skeleton variant="text" width={150} height={30} animation="wave" />
					<Skeleton variant="text" width={200} height={30} animation="wave" />
				</div>
			</div>
		</Box>
	);
}

function TableOmniWithSkeleton(
	{
		rows,
		headCells,
		title,
		tableName,
		checkBox,
		selectedRows,
		setSelectedRows,
		setCurrentParams,
		perPageRows,
		curPage,
		totalItems,
		onEditClick,
		onDeleteClick,
		isLoadingPagination,
		rowKey,
		height = '100%',
		rowsPerPageOptions = [5, 10, 25],
		loading = false,
		collapsible = false,
		defaultCollapsed = false,
		emptyMessage = "No Data Found!"
	},
	props
) {
	const classes = useStyles(props);
	const theme = useTheme();

	const [order, setOrder] = React.useState('desc');
	const [orderBy, setOrderBy] = React.useState(headCells[0].id);
	const [page, setPage] = React.useState(curPage || 0);
	const [rowsPerPage, setRowsPerPage] = React.useState(perPageRows || 5);
	const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const getRowKey = useCallback(
		row => {
			if (!rowKey) return row.id;
			if (typeof rowKey === 'function') return rowKey(row);
			return row[rowKey];
		},
		[rowKey]
	);

	const handleSelectAllClick = event => {
		if (event.target.checked) {
			const newSelected = rows.map(n => getRowKey(n));
			setSelectedRows(newSelected);
			return;
		}
		setSelectedRows([]);
	};

	const handleClick = (event, key) => {
		const selectedIndex = selectedRows.indexOf(key);
		let newSelected = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selectedRows, key);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selectedRows.slice(1));
		} else if (selectedIndex === selectedRows.length - 1) {
			newSelected = newSelected.concat(selectedRows.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selectedRows.slice(0, selectedIndex),
				selectedRows.slice(selectedIndex + 1)
			);
		}
		setSelectedRows(newSelected);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
		if (setCurrentParams) {
			setCurrentParams({ limit: rowsPerPage, page: newPage });
		}
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		if (setCurrentParams) {
			setCurrentParams({ limit: parseInt(event.target.value, 10), page: 0 });
		}
	};

	const handleToggleCollapse = () => {
		setIsCollapsed(!isCollapsed);
	};

	const visibleRows = React.useMemo(() => {
		if (totalItems) {
			return rows;
		}
		return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
	}, [totalItems, page, rowsPerPage, rows]);

	const getValue = (row, id) => {
		const cellConfig = headCells.find(cell => cell.id === id);

		if (cellConfig?.customRow) {
			return cellConfig.customRow(row);
		}

		const keys = id.split('.');
		const value = keys.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ''), row);

		if (id === 'dt' && value) {
			return moment(value).format('DD-MM-YYYY hh:mm a');
		}
		if (id === 'edit') {
			return <EditIcon style={{ cursor: 'pointer' }} fontSize="large" onClick={() => onEditClick(row)} />;
		}
		if (id === 'delete') {
			return <DeleteIcon style={{ cursor: 'pointer' }} fontSize="large" onClick={() => onDeleteClick(row)} />;
		}

		return value;
	};

	// Empty state rendering
	if (!loading && rows.length === 0) {
		return (
			<Box sx={{ width: '100%', height: height }} className={classes.tableContainer}>
				<Paper className={classes.paper} sx={{ width: '100%', mb: 2, boxShadow: 'none' }}>
					{tableName && (
						<EnhancedTableToolbar 
							numSelected={0} 
							title={title}
							collapsible={collapsible}
							isCollapsed={isCollapsed}
							onToggleCollapse={handleToggleCollapse}
						/>
					)}
					<div className={classes.emptyState}>
						<Typography variant="h5" color="textSecondary">
							{emptyMessage}
						</Typography>
					</div>
				</Paper>
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%', height: height }} className={classes.tableContainer}>
			<Paper className={classes.paper} sx={{ width: '100%', mb: 0, boxShadow: 'none' }}>
				{tableName && (
					<EnhancedTableToolbar 
						numSelected={selectedRows?.length || 0} 
						title={title}
						collapsible={collapsible}
						isCollapsed={isCollapsed}
						onToggleCollapse={handleToggleCollapse}
					/>
				)}
				
				<Collapse in={!isCollapsed} timeout="auto" className={classes.collapseContainer}>
					{loading ? (
						<TableSkeleton 
							headCells={headCells} 
							checkBox={checkBox}
							rowsPerPage={rowsPerPage}
						/>
					) : (
						<>
							<div className={classes.tableWrapper}>
								<Table className={classes.table} aria-labelledby="tableTitle" size="medium" stickyHeader>
									<EnhancedTableHead
										numSelected={selectedRows?.length}
										order={order}
										orderBy={orderBy}
										onSelectAllClick={handleSelectAllClick}
										onRequestSort={handleRequestSort}
										rowCount={rows.length}
										headCells={headCells}
										checkBox={checkBox}
									/>
									<TableBody>
										{visibleRows.map((row, index) => {
											const key = getRowKey(row);
											const isItemSelected = selectedRows?.includes(key);
											const labelId = `enhanced-table-checkbox-${index}`;

											return (
												<TableRow
													hover
													onClick={checkBox ? event => handleClick(event, key) : null}
													role="checkbox"
													aria-checked={isItemSelected}
													tabIndex={-1}
													key={key}
													selected={isItemSelected}
													className={classes.tableRow}
												>
													{checkBox && (
														<TableCell padding="checkbox" className={classes.tableCell}>
															<Checkbox
																checked={isItemSelected}
																inputProps={{
																	'aria-labelledby': labelId
																}}
																className={classes.checkbox}
																style={{
																	color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff'
																}}
															/>
														</TableCell>
													)}
													{headCells.map(cell => (
														<TableCell
															classes={{ root: classes.tableCell }}
															sx={{ fontSize: '1.1rem' }}
															key={cell.id}
															align={cell.numeric ? 'center' : 'left'}
														>
															{getValue(row, cell.id)}
														</TableCell>
													))}
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</div>
							{!isLoadingPagination && (
								<div className={classes.pagination}>
									<TablePagination
										rowsPerPageOptions={rowsPerPageOptions}
										component="div"
										count={totalItems || rows.length}
										rowsPerPage={rowsPerPage}
										page={page}
										onPageChange={handleChangePage}
										onRowsPerPageChange={handleChangeRowsPerPage}
										ActionsComponent={PaginationActionsTableOmni}
									/>
								</div>
							)}
						</>
					)}
				</Collapse>
			</Paper>
		</Box>
	);
}

TableOmniWithSkeleton.propTypes = {
	rows: PropTypes.array.isRequired,
	headCells: PropTypes.array.isRequired,
	title: PropTypes.string,
	height: PropTypes.string,
	loading: PropTypes.bool,
	collapsible: PropTypes.bool,
	defaultCollapsed: PropTypes.bool,
	emptyMessage: PropTypes.string
};

export default TableOmniWithSkeleton;